import { Chip, Stack, Typography } from '@montugroup/design-system';
import type { ConsultationResponseDto, ConsultationWaitlistDto } from '@montugroup/pms-api-contracts';

import { useWaitlistPreferenceForm } from '@/pages/consultations/hooks/useWaitlistPreferenceForm';
import { AddMobilePrompt } from './AddMobilePrompt';
import { TextButton } from './ConsultationWaitlistSection.styles';
import { LeaveWaitlistButton } from './LeaveWaitlistButton';
import { WAITLIST_LEFT_MESSAGE, WAITLIST_PREFERENCE_SAVED_MESSAGE, waitlistAvailabilityLabel } from './waitlist.config';
import { WaitlistPreferenceForm } from './WaitlistPreferenceForm';

export interface WaitlistMembershipProps {
  consultation: ConsultationResponseDto;
  waitlist: ConsultationWaitlistDto;
  /** called with the message to announce after a change or leave */
  onOutcome: (message: string) => void;
  /** called when pms-service refuses the change; the section explains it, as the refetch may unmount this */
  onRefused: () => void;
  /** called when the patient opens the form or leaves, which replaces an earlier refusal */
  onAction: () => void;
}

/**
 * Shows the patient's place on the waitlist, and lets them change their preference or leave. Offers are only sent and
 * accepted by text, so they aren't shown here.
 */
export function WaitlistMembership({
  consultation,
  waitlist,
  onOutcome,
  onRefused,
  onAction,
}: WaitlistMembershipProps) {
  const { isFormOpen, open, formProps, buttonFocusProps } = useWaitlistPreferenceForm({
    consultation,
    mode: 'change',
    successMessage: WAITLIST_PREFERENCE_SAVED_MESSAGE,
    onOutcome,
    onRefused,
    onAction,
  });

  if (isFormOpen) {
    return <WaitlistPreferenceForm initialAvailability={waitlist.availability} submitLabel="Save" {...formProps} />;
  }

  return (
    <Stack gap={2} alignItems="flex-start">
      <Chip
        color="info"
        label={`On the waitlist${waitlist.availability ? ` · ${waitlistAvailabilityLabel(waitlist.availability)}` : ''}`}
      />
      {waitlist.ineligibleReason === 'NO_MOBILE' ? (
        <AddMobilePrompt>so we can text you earlier times.</AddMobilePrompt>
      ) : (
        <Typography variant="body1">We&apos;ll text you if an earlier time becomes available.</Typography>
      )}
      <Stack direction="row" gap={3}>
        {/* pms-service refuses preference changes once the consultation is ineligible */}
        {waitlist.eligible && (
          <TextButton type="button" {...buttonFocusProps} onClick={open}>
            Change preference
          </TextButton>
        )}
        <LeaveWaitlistButton
          consultation={consultation}
          availability={waitlist.availability}
          onLeft={() => {
            onAction();
            onOutcome(WAITLIST_LEFT_MESSAGE);
          }}
        />
      </Stack>
    </Stack>
  );
}
